<script setup lang="ts">
import { computed, toRef } from "vue";
import { storeToRefs } from "pinia";
import { Select } from "@/components/episto-ui/select";
import OptionRow from "./OptionRow.vue";
import { useCardLocalOptions } from "@/composables/useCardLocalOptions";
import { useResultsV2PopulationsStore } from "@stores/survey/resultsV2/resultsV2.populations";
import { customPopulationsWith, extraPopulationTokens } from "@/components/Survey/ResultsV2/cardOptionRules";

const props = defineProps({
  card: { type: Object, required: true },
});

const cardRef = toRef(props, "card");
const { store, localOptions, updateLocalOption } = useCardLocalOptions(cardRef);
const { savedPopulations } = storeToRefs(store);
const { populations: viewPopulations } = storeToRefs(useResultsV2PopulationsStore());

const viewTokens = computed(() => viewPopulations.value.map((p) => p.search_group?.token).filter(Boolean));

const populationOptions = computed(() =>
  savedPopulations.value
    .filter((sp) => !viewTokens.value.includes(sp.token))
    .map((sp) => ({ value: sp.token, label: sp.name })),
);

const extraTokens = computed(() => extraPopulationTokens(localOptions.value.custom_populations, viewTokens.value));

async function setExtraTokens(value: unknown) {
  const tokens = Array.isArray(value) ? value.map(String) : [];
  const added = tokens.filter((token) => !extraTokens.value.includes(token));
  const removed = extraTokens.value.filter((token) => !tokens.includes(token));
  updateLocalOption("custom_populations", customPopulationsWith(tokens, viewTokens.value));
  if (removed.includes(localOptions.value.population)) updateLocalOption("population", null);
  for (const token of added) {
    const sp = savedPopulations.value.find((s) => s.token === token);
    if (sp) await store.fetchPopulationChartData(sp);
  }
}
</script>

<template>
  <OptionRow
    label="Populations"
    hint="The dashboard populations always show on this card. Add here another population that matters for this analysis only.">
    <Select
      :model-value="extraTokens"
      :options="populationOptions"
      placeholder="Add a population for this card..."
      multiple
      @update:model-value="setExtraTokens" />
  </OptionRow>
</template>
